import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../hooks/useTheme";
import { useLayout } from "../../hooks/useLayout";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import { Toast } from "primereact/toast";
import ChangePasswordDialog from "../authentication/ChangePasswordDialog";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import axiosWithAuth from "../../helpers/AxiosHelper";
import errorMessage from "../../helpers/errorMessage";
import { check_admin_access, conv_groups_array_for_state } from "../../helpers/AccessHelper";
import env from "../../env.json";
import { get_route_trail, GUIDE_MENU_ITEMS } from "./navConfig";

import "./navigation_bar.css";

const NavBar = (props) => {
  const navigate = useNavigate();
  const { user, platform, groups, real_groups, is_viewing_as, view_as, stop_view_as } = useAuth();
  const { theme, setTheme } = useTheme();
  const { collapsed, setCollapsed, nav_open, setNavOpen } = useLayout();
  const location = useLocation();
  const account_menu = useRef(null);
  // User Guide (info sheets/templates/guides) - moved here from the sidebar,
  // so the nav itself only lists pages.
  const guide_menu = useRef(null);
  const toast = useRef(null);
  const [change_password_open, setChangePasswordOpen] = useState(false);
  const [platforms_list, setPlatformsList] = useState([]);
  const [view_as_platform, setViewAsPlatform] = useState("");
  const [view_as_loading, setViewAsLoading] = useState(false);

  // The real admin identity is `real_groups` while impersonating (groups
  // itself has been swapped to the viewed-as platform's groups by then) and
  // `groups` otherwise.
  // env.DEBUG is dev/local only (envs/dev.json has DEBUG:true, envs/prod.json
  // has DEBUG:false - see App.js/AccessHelper.js's InternalDevelopers bypass,
  // same flag) - View As is a dev/testing convenience, not something to leave
  // reachable in prod even for an Admin, so it's absent there entirely
  // rather than just disabled.
  const is_real_admin = env.DEBUG && check_admin_access(is_viewing_as ? real_groups : groups);

  useEffect(() => {
    if (!is_real_admin) {
      return;
    }
    axiosWithAuth.get("/platforms")
      .then((data) => setPlatformsList(data.data || []))
      .catch(err => console.error('Originator list fetch failed:', errorMessage(err)));
  }, [is_real_admin]);

  // App.js sits *above* AuthProvider (it renders <AuthProvider> itself), so
  // it can't call useAuth() for its own route-gating groups/platform state -
  // this is the bridge, keeping App's copies in sync with every change here
  // (login, logout, View As start/stop alike), not just the ones start_view_as/
  // end_view_as used to poke at directly (which only ever touched setGroups,
  // never setPlatform - see App.js for what that broke: Sidebar.js's nav
  // (reads useAuth() directly) correctly hid Platform Report while viewing as
  // a platform, but App.js's own route for it stayed mounted the whole time
  // regardless, since its `platform` check never updated).
  useEffect(() => {
    props.setGroups(groups);
    props.setPlatform(platform);
    props.setIsViewingAs(is_viewing_as);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, platform, is_viewing_as]);

  // Theme switching lives on the topbar's own sun/moon button, so the menu
  // only carries account actions.
  const account_menu_items = [
    { label: !platform || platform.toLowerCase() === "exaloan" ? "Exaloan" : `Originator: ${platform}`, disabled: true },
    { separator: true },
    { label: "Change password", icon: "pi pi-key", command: () => setChangePasswordOpen(true) },
    { separator: true },
    { label: "Logout", icon: "pi pi-sign-out", command: () => navigate("/logout") },
  ];

  // Fetches the real Cognito groups for every user account on the chosen
  // platform (a platform can have more than one account) and unions them,
  // so View As reflects everything that platform's accounts can actually
  // see rather than picking one arbitrarily. A platform with no Cognito
  // accounts yet just previews with no groups, matching a freshly
  // onboarded client with nothing enabled.
  const start_view_as = () => {
    if (!view_as_platform) {
      return;
    }
    setViewAsLoading(true);
    Promise.all([
      axiosWithAuth.get(`/platforms/${view_as_platform}/users`),
      axiosWithAuth.get("/platforms/config", { params: { platform: view_as_platform } }),
    ]).then(([users_res, config_res]) => {
      let real_cognito_users = users_res.data || [];
      let union_of_groups = [...new Set(real_cognito_users.flatMap(u => u.groups || []))];
      if (real_cognito_users.length === 0) {
        alert(`${view_as_platform} has no Cognito user accounts yet - previewing with no groups (baseline access only).`);
      }
      let groups_for_state = conv_groups_array_for_state(union_of_groups);
      view_as(view_as_platform, groups_for_state, (config_res.data || [])[0] || null);
      setViewAsPlatform("");
      navigate("/");
    }).catch(err => {
      toast.current.show({ severity: "error", summary: "View As failed", detail: errorMessage(err), life: 6000 });
    }).finally(() => setViewAsLoading(false));
  };

  const end_view_as = () => {
    stop_view_as();
    navigate("/");
  };

  // Nothing to show before sign-in (the login page has its own brand
  // panel) - the component itself still stays mounted regardless, since the
  // effects above keep App.js's route-gating state in sync.
  if (!user) {
    return null;
  }

  const trail = get_route_trail(location.pathname);
  const page_title = trail[trail.length - 1] || "ExaPort";
  const is_dark = theme === "dark";

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button
          type="button"
          className="topbar-icon-btn topbar-menu-btn"
          aria-label="Open navigation"
          onClick={() => setNavOpen(!nav_open)}
        >
          <i className="pi pi-bars" />
        </button>
        <button
          type="button"
          className="topbar-icon-btn topbar-collapse-btn"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setCollapsed(!collapsed)}
        >
          <i className="pi pi-bars" />
        </button>
        <div className="topbar-heading">
          {trail.length > 1 && (
            <ol className="topbar-breadcrumb" aria-label="Breadcrumb">
              {trail.slice(0, -1).map((crumb, i) => (
                <li key={`${crumb}-${i}`}>{crumb}</li>
              ))}
            </ol>
          )}
          <h1 className="topbar-title">{page_title}</h1>
        </div>
      </div>

      <div className="topbar-right">
        {is_viewing_as && (
          <div className="topbar-view-as-banner">
            <span className="topbar-view-as-dot" />
            <span>Viewing as <strong>{platform}</strong></span>
            <Button label="Stop" icon="pi pi-times" className="p-button-sm p-button-text" onClick={end_view_as} />
          </div>
        )}
        {is_real_admin && !is_viewing_as && (
          <div className="topbar-view-as">
            <i className="pi pi-eye topbar-view-as-icon" />
            <Dropdown
              inputId="viewAsPlatform"
              value={view_as_platform}
              optionLabel="platform"
              optionValue="platform"
              options={platforms_list}
              onChange={(e) => setViewAsPlatform(e.value)}
              placeholder="View as Originator..."
              className="topbar-view-as-dropdown"
            />
            <Button label="View As" className="p-button-sm" disabled={!view_as_platform} loading={view_as_loading} onClick={start_view_as} />
          </div>
        )}
        <button
          type="button"
          className="topbar-help-btn"
          aria-haspopup="menu"
          title="User Guide - information sheets, templates and guides"
          onClick={(e) => guide_menu.current.toggle(e)}
        >
          <i className="pi pi-book" />
          <span className="topbar-help-label">User Guide</span>
        </button>
        <OverlayPanel ref={guide_menu} id="topbar_guide_menu" className="topbar-guide-panel">
          <div className="guide-panel-header">
            <span className="guide-panel-title">User Guide</span>
            <span className="guide-panel-subtitle">Information sheets, templates and guides - each opens in a new tab.</span>
          </div>
          <div className="guide-panel-groups">
            {GUIDE_MENU_ITEMS.map(group => (
              <div className="guide-panel-group" key={group.label}>
                <div className="guide-panel-group-label"><i className={group.icon} />{group.label}</div>
                {group.items.map(item => (
                  <a key={item.label} className="guide-panel-link" href={item.url} target={item.target} rel="noopener noreferrer" onClick={() => guide_menu.current.hide()}>
                    <span>{item.label}</span>
                    <i className="pi pi-external-link" />
                  </a>
                ))}
              </div>
            ))}
          </div>
        </OverlayPanel>
        <button
          type="button"
          className="topbar-icon-btn"
          aria-label={is_dark ? "Switch to Light Mode" : "Switch to Dark Mode"}
          title={is_dark ? "Switch to Light Mode" : "Switch to Dark Mode"}
          onClick={() => setTheme(is_dark ? "light" : "dark")}
        >
          <i className={`pi ${is_dark ? "pi-sun" : "pi-moon"}`} />
        </button>
        <button
          type="button"
          className="topbar-account"
          aria-haspopup="menu"
          onClick={(e) => account_menu.current.toggle(e)}
        >
          <span className="topbar-avatar">{String(user).slice(0, 1).toUpperCase()}</span>
          <span className="topbar-account-meta">
            <span className="topbar-account-name">{String(user).split("@")[0]}</span>
            {platform && <span className="topbar-account-platform">{platform}</span>}
          </span>
          <i className="pi pi-angle-down topbar-account-chevron" />
        </button>
        <Menu model={account_menu_items} popup ref={account_menu} id="navbar_account_menu" className="topbar-account-menu" />
        <ChangePasswordDialog
          visible={change_password_open}
          onHide={() => setChangePasswordOpen(false)}
          onChanged={() => toast.current.show({ severity: "success", summary: "Password changed", detail: "Use your new password next time you sign in.", life: 5000 })}
        />
        <Toast ref={toast} position="top-right" />
      </div>
    </header>
  );
};

export default NavBar;
